/**
 * Multi-territory security sanitizers (centralized data-validation routines).
 *
 * These helpers neutralize two classes of Veracode findings:
 *
 *  - CWE-80 (Basic XSS): download filenames derived from untrusted sources
 *    (server `Content-Disposition`, API row data, user input) must not be
 *    placed into DOM sinks (`<a download>` / `href`) without sanitization.
 *
 *  - CWE-117 (Improper Output Neutralization for Logs / log forging): untrusted
 *    values written to `console.*` must have CR/LF and other control characters
 *    stripped so an attacker cannot forge or inject additional log lines.
 */

/* eslint-disable no-control-regex */
// Matches ASCII control characters (0x00–0x1F and 0x7F), including CR and LF.
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g
/* eslint-enable no-control-regex */

/**
 * Sanitize a filename before using it as the `download` attribute / href of an
 * anchor element. Prevents DOM-based XSS and path-traversal in the saved name:
 *   - strips directory separators (`/`, `\`) and parent refs (`..`)
 *   - strips control characters (incl. CR/LF) and angle brackets / quotes that
 *     could break out of the attribute context
 *   - collapses whitespace and trims
 *   - falls back to a safe default when nothing usable remains
 */
export function sanitizeFilename(
  filename: unknown,
  fallback = 'download.xlsx'
): string {
  if (typeof filename !== 'string') {
    return fallback
  }

  const cleaned = filename
    // Keep only the final path segment — drop any directory components.
    .replace(/^.*[\\/]/, '')
    // Remove control chars (CR/LF/tab/etc.).
    .replace(CONTROL_CHARS, '')
    // Strip the script-related HTML tag characters that drive Basic XSS
    // (CWE-80): angle brackets, ampersand, quotes, backtick and other chars
    // that are unsafe in a filename / HTML attribute context. Removing them
    // outright means the value can never form or break out of an HTML tag or
    // attribute when placed in a DOM sink.
    .replace(/[<>&"'`|?*:;=(){}[\]]/g, '')
    // Collapse parent-directory sequences.
    .replace(/\.{2,}/g, '.')
    // Collapse internal whitespace runs.
    .replace(/\s+/g, ' ')
    .trim()

  return cleaned.length > 0 ? escapeHtml(cleaned) : fallback
}

/**
 * Contextual HTML-entity escaping (CWE-80 Basic XSS). Converts the five
 * characters that are significant in HTML — `&`, `<`, `>`, `"`, `'` — into
 * their entity equivalents so the value cannot be interpreted as script-related
 * markup when written into any portion of an HTTP response / DOM node. This is
 * the OWASP-recommended output-neutralization step and acts as the recognized
 * cleanser for untrusted strings before they reach a DOM sink.
 */
export function escapeHtml(value: unknown): string {
  if (typeof value !== 'string') {
    return ''
  }
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/`/g, '&#x60;')
}

/**
 * Sanitize an arbitrary value for safe logging (CWE-117). Objects/errors are
 * serialized to a single line; CR/LF and control characters are removed so the
 * value cannot inject or forge additional log entries. The result is also
 * length-capped to avoid flooding the log from attacker-controlled input.
 */
export function sanitizeForLog(value: unknown, maxLength = 2000): string {
  let text: string

  if (value === null || value === undefined) {
    text = String(value)
  } else if (value instanceof Error) {
    text = `${value.name}: ${value.message}`
  } else if (typeof value === 'object') {
    try {
      text = JSON.stringify(value)
    } catch {
      text = '[unserializable object]'
    }
  } else {
    text = String(value)
  }

  const sanitized = text.replace(CONTROL_CHARS, ' ').trim()
  return sanitized.length > maxLength
    ? `${sanitized.slice(0, maxLength)}…[truncated]`
    : sanitized
}

/**
 * Sanitize a URL before assigning it to a DOM sink such as an `<iframe src>`,
 * anchor `href`, or `window.location`. Prevents DOM-based XSS (CWE-80) via
 * dangerous schemes like `javascript:`, `data:`, `vbscript:`, and `blob:` that
 * can execute script in the page/frame context.
 *
 * Only absolute `http(s)` URLs and same-origin relative URLs are allowed. An
 * optional `allowedOrigin` further constrains absolute URLs to a single origin
 * (e.g. the embedded app base). Anything else returns the `fallback`.
 */
export function sanitizeUrl(
  url: unknown,
  options: { allowedOrigin?: string; fallback?: string } = {}
): string {
  const { allowedOrigin, fallback = '' } = options

  if (typeof url !== 'string') {
    return fallback
  }

  // Strip control characters (incl. CR/LF/tab) that can be used to smuggle a
  // dangerous scheme past naive checks, e.g. "java\nscript:alert(1)".
  const trimmed = url.replace(CONTROL_CHARS, '').trim()
  if (trimmed.length === 0) {
    return fallback
  }

  // Reject any explicit non-http(s) scheme outright.
  const schemeMatch = /^([a-z][a-z0-9+.-]*):/i.exec(trimmed)
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase()
    if (scheme !== 'http' && scheme !== 'https') {
      return fallback
    }
  }

  try {
    // Resolve against the allowed origin (when supplied) so relative routes are
    // supported while absolute URLs are still validated below.
    const base = allowedOrigin || undefined
    const parsed = new URL(trimmed, base)

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return fallback
    }

    if (allowedOrigin) {
      const allowed = new URL(allowedOrigin)
      if (parsed.origin !== allowed.origin) {
        return fallback
      }
    }

    return parsed.toString()
  } catch {
    return fallback
  }
}
